import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  Typography,
  Paper,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext/AuthContext';
import buildingImage from './../../../Images/house.jpeg';
import CustomSearchBar from '../Lease Module/CustomSearchBar';
import ReUsableCard from '../Lease Module/ReUsableCard';
import CustomBottomNav from './BottomNavbar/BottomNavbar';
import { BASE_URL } from '../../Api/ApiUrls';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

const rentalTypes = [
  "1BHK", "2BHK", "3BHK", "4+ BHK", "PLOT/LAND", "DUPLEX HOUSE",
  "COMMERCIAL LAND", "COMMERCIAL BUILDING/ Space", "VILLA",
  "PG-SCHOOL-OFFICE", "SHOPPING mall/shop"
];

const Rent_Property_Map = () => {
  // State declarations
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [visibleProperties, setVisibleProperties] = useState([]); // NEW: Properties in current map view
  const [selectedType, setSelectedType] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [searchRadius] = useState(10000);
  const [saved, setSaved] = useState(() => {
    const stored = localStorage.getItem('saveRent');
    return stored ? JSON.parse(stored) : [];
  });
  const [categories, setCategories] = useState([]);
  const [likedCards, setLikedCards] = useState({});
  const { userId } = useContext(AuthContext);
  const [savedProperties, setSavedProperties] = useState([]);
  const [saving, setSaving] = useState({});
  const navigate = useNavigate();
  const [priceFilter, setPriceFilter] = useState(null);
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [maxZoom, setMaxZoom] = useState(11);
  const [mapKey, setMapKey] = useState(0);

  const cardListRef = useRef(null);
  const [activePropertyId, setActivePropertyId] = useState(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollRafRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const isPointerOverCarouselRef = useRef(false);

  const skipMapPanRef = useRef(false);
  const scrollDebounceRef = useRef(null);
  const lastActiveIdRef = useRef(null);

  const [currentBounds, setCurrentBounds] = useState(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  // subscription check
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/Get-user-plan/${userId}/`);
        const userSubscriptions = response.data;
        const hasTenantSubscription = Array.isArray(userSubscriptions) &&
          userSubscriptions.some(sub => sub.user_type === "Tenant");
        if (hasTenantSubscription) {
          setIsSubscribed(true);
          setMaxZoom(20);
        } else {
          setIsSubscribed(false);
          setMaxZoom(11);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        setIsSubscribed(false);
        setMaxZoom(11);
      }
    };

    if (userId) checkSubscription();
    else {
      setIsSubscribed(false);
      setMaxZoom(11);
    }
  }, [userId]);

  // helper: marker color by age — overridden to green (user requested all dots green)
  const getMarkerColor = (createdAt) => {
    return '#008000';
  };

  // helper: price label formatting
  const getPriceLabel = (price) => {
    if (price >= 10000000) return `${Math.round(price / 10000000)} Cr`;
    if (price >= 100000) return `${Math.round(price / 100000)} L`;
    if (price >= 1000) return `${Math.round(price / 1000)} K`;
    return `${price}`;
  };

  // create marker icon
  const createMarkerIconForProperty = (property, active = false) => {
    try {
      const currentZoom = map ? map.getZoom() : 13;
      const showPriceLabels = currentZoom >= 13;
      const price = parseInt(String(property.price || '').replace(/[^\d]/g, '')) || 0;
      const priceLabel = showPriceLabels ? getPriceLabel(price) : null;
      const color = active ? '#000000' : getMarkerColor(property.createdAt);
      const borderColor = active ? '#FF0000' : '#FFFFFF';
      const baseSize = priceLabel ? 28 : 12;
      const scale = active ? 1.6 : 1;
      const size = baseSize * scale;
      const translate = -(size / 2);
      const fontSize = priceLabel ? 10 * scale : 0;
      return L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            background-color: ${color};
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            border: 2px solid ${borderColor};
            display: flex;
            justify-content: center;
            align-items: center;
            color: white;
            font-weight: bold;
            font-size: ${fontSize}px;
            transform: translate(${translate}px, ${translate}px);
            transition: transform 0.12s ease, width 0.12s ease, height 0.12s ease;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          ">
            ${priceLabel || ''}
          </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    } catch (err) {
      console.error('createMarkerIconForProperty error', err);
      return L.divIcon({ className: 'custom-marker' });
    }
  };

  // NEW: Sort properties by distance from searchLocation or map center
  const sortPropertiesByDistance = (props, centerLat, centerLng) => {
    if (!centerLat || !centerLng) return props;
    return props
      .map(prop => {
        if (!prop.lat || !prop.lng) return prop;
        const distance = L.latLng([prop.lat, prop.lng]).distanceTo([centerLat, centerLng]);
        return { ...prop, distance };
      })
      .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
  };

  // NEW: Filter properties that are within current map bounds
  const getPropertiesInBounds = (props, bounds) => {
    if (!bounds) return props;
    
    return props.filter(property => {
      if (!property.lat || !property.lng) return false;
      
      try {
        const propertyLatLng = L.latLng(property.lat, property.lng);
        return bounds.contains(propertyLatLng);
      } catch (error) {
        console.error('Error checking bounds for property:', property.id, error);
        return false;
      }
    });
  };

  // Initialize Leaflet Map - FIXED CLEANUP
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    let leafletMap = null;
    
    try {
      const hyderabadCoords = [17.3850, 78.4867];
      const initialCoords = searchLocation ? [searchLocation.lat, searchLocation.lng] : hyderabadCoords;
      const initialZoom = searchLocation ? 12 : 7;



console.log('Initializing map with:', initialCoords, 'zoom:', initialZoom);

leafletMap = L.map(mapContainerRef.current, {
  center: initialCoords,
  zoom: initialZoom,
  zoomControl: false,
  maxZoom: maxZoom  // ← USE THE STATE VARIABLE INSTEAD
});

const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxNativeZoom: maxZoom  // ← USE THE STATE VARIABLE INSTEAD
}).addTo(leafletMap);
     
      // moved to top right to avoid search bar overlap
      L.control.zoom({ position: 'topright' }).addTo(leafletMap);

      const handleZoomEnd = () => {
        try {
          const currentZoom = leafletMap.getZoom();
          if (!isSubscribed && currentZoom > 11) {
            leafletMap.setZoom(11);
          }
          
          // Update visible properties based on new bounds
          const bounds = leafletMap.getBounds();
          setCurrentBounds(bounds);
          
          // update marker icons so price labels reflect zoom change
          setTimeout(() => {
            setMarkers(prevMarkers => {
              prevMarkers.forEach(m => {
                const pid = m.options.propertyId;
                const prop = filteredProperties.find(p => p.id === pid);
                if (prop && leafletMap.hasLayer(m)) {
                  m.setIcon(createMarkerIconForProperty(prop, pid === activePropertyId));
                }
              });
              return [...prevMarkers];
            });
          }, 50);
        } catch (error) {
          console.error('Error handling zoom:', error);
        }
      };

      const handleMoveEnd = () => {
        try {
          const bounds = leafletMap.getBounds();
          setCurrentBounds(bounds);
        } catch (error) {
          console.error('Error handling move end:', error);
        }
      };

      leafletMap.on('zoomend', handleZoomEnd);
      leafletMap.on('moveend', handleMoveEnd);

      // Set initial bounds
      setCurrentBounds(leafletMap.getBounds());

      setMap(leafletMap);
      setMapLoaded(true);
    } catch (error) {
      console.error('Error initializing map:', error);
    }

    return () => {
      if (leafletMap) {
        try {
          leafletMap.off('zoomend');
          leafletMap.off('moveend');
          
          leafletMap.eachLayer(layer => {
            try {
              leafletMap.removeLayer(layer);
            } catch (e) {
            }
          });
          
          // Remove the map from DOM
          if (leafletMap._container && leafletMap._container.parentNode) {
            leafletMap.remove();
          }
        } catch (error) { 
          console.error('Error cleaning up map:', error); 
        }
      }
    };
  }, [isSubscribed, maxZoom, searchLocation, mapKey]);

  // NEW: Handle search location changes - MAP SHOULD MOVE TO SEARCHED LOCATION
  useEffect(() => {
    if (map && searchLocation) {
      try {
        console.log('Moving map to searched location:', searchLocation);
        
        // Pan to the searched location
        map.panTo([searchLocation.lat, searchLocation.lng], { 
          animate: true, 
          duration: 1 
        });
        
        // Set appropriate zoom level for the searched location
        map.setZoom(12.5);
        
        if (window.searchLocationMarker) {
          map.removeLayer(window.searchLocationMarker);
        }
        
        // Add a custom marker for the searched location
        const searchIcon = L.divIcon({
          className: 'search-location-marker',
          html: ``,
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
        
        window.searchLocationMarker = L.marker([searchLocation.lat, searchLocation.lng], {
          icon: searchIcon
        }).addTo(map)
        
          
      } catch (error) {
        console.error('Error updating map to search location:', error);
      }
    }
  }, [searchLocation, map]);

  // NEW: Update visible properties when bounds or filtered properties change
  useEffect(() => {
    if (currentBounds && filteredProperties.length > 0) {
      const propertiesInView = getPropertiesInBounds(filteredProperties, currentBounds);
      setVisibleProperties(propertiesInView);
      
      // If active property is no longer in view, clear it
      if (activePropertyId && !propertiesInView.find(p => p.id === activePropertyId)) {
        setActivePropertyId(null);
      }
    } else {
      setVisibleProperties(filteredProperties);
    }
  }, [currentBounds, filteredProperties]);

  // Handle map recreation when filters are cleared
  useEffect(() => {
    if (filteredProperties.length > 0 && !priceFilter && !selectedType && !mapLoaded) {
      setTimeout(() => {
        if (mapContainerRef.current) {
          setMapKey(prev => prev + 1);
        }
      }, 100);
    }
  }, [filteredProperties, priceFilter, selectedType, mapLoaded]);

  // NEW: Check scroll position for infinite scroll
  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const checkScrollPosition = () => {
      if (!container || visibleProperties.length === 0) return;
      
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      
      // Check if at start
      setIsAtStart(scrollLeft <= 10);
      
      // Check if at end (with a small buffer)
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 10);
    };

    const handleScroll = () => {
      checkScrollPosition();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    checkScrollPosition();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [visibleProperties]);

  // NEW: Handle infinite scroll for left arrow
  const handleLeftArrowClick = () => {
    const container = cardListRef.current;
    if (!container || visibleProperties.length === 0) return;

    if (isAtStart) {
      // If at start, scroll to the end
      container.scrollTo({
        left: container.scrollWidth,
        behavior: 'smooth'
      });
    } else {
      // Normal scroll left
      container.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  // NEW: Handle infinite scroll for right arrow
  const handleRightArrowClick = () => {
    const container = cardListRef.current;
    if (!container || visibleProperties.length === 0) return;

    if (isAtEnd) {
      // If at end, scroll to the start
      container.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
    } else {
      // Normal scroll right
      container.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  // Utility functions to disable/enable map interactions while dragging carousel
  const disableMapInteractions = () => {
    if (!map) return;
    try {
      if (map.dragging && map.dragging.enabled()) map.dragging.disable();
      if (map.touchZoom && map.touchZoom.enabled()) map.touchZoom.disable();
      if (map.doubleClickZoom && map.doubleClickZoom.enabled()) map.doubleClickZoom.disable();
      if (map.scrollWheelZoom && map.scrollWheelZoom.enabled()) map.scrollWheelZoom.disable();
    } catch (err) { /* ignore */ }
  };
  
  const enableMapInteractions = () => {
    if (!map) return;
    try {
      if (map.dragging && !map.dragging.enabled()) map.dragging.enable();
      if (map.touchZoom && !map.touchZoom.enabled()) map.touchZoom.enable();
      if (map.doubleClickZoom && !map.doubleClickZoom.enabled()) map.doubleClickZoom.enable();
      if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) map.scrollWheelZoom.enable();
    } catch (err) { /* ignore */ }
  };

  // Attach pointer/touch listeners to carousel element to disable map while interacting with carousel
  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const onPointerDown = (e) => {
      isPointerOverCarouselRef.current = true;
      disableMapInteractions();
    };
    const onPointerUp = (e) => {
      isPointerOverCarouselRef.current = false;
      setTimeout(() => enableMapInteractions(), 40);
    };
    const onPointerCancel = () => {
      isPointerOverCarouselRef.current = false;
      setTimeout(() => enableMapInteractions(), 40);
    };

    container.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    container.addEventListener('pointercancel', onPointerCancel, { passive: true });
    container.addEventListener('touchcancel', onPointerCancel, { passive: true });

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerCancel);
      container.removeEventListener('touchcancel', onPointerCancel);
    };
  }, [map, cardListRef.current]);

  // Render markers when map + filteredProperties change
  useEffect(() => {
    if (!mapLoaded || !map) return;

    // remove previous markers cleanly
    markers.forEach(m => { 
      try { 
        if (map.hasLayer(m)) map.removeLayer(m); 
      } catch (e) {
        // Ignore errors during marker cleanup
      } 
    });
    setMarkers([]);

    const newMarkers = [];

    filteredProperties
      .filter(p => p.lat && p.lng)
      .forEach(property => {
        try {
          const icon = createMarkerIconForProperty(property, property.id === activePropertyId);
          const marker = L.marker([property.lat, property.lng], {
            icon,
            propertyId: property.id
          }).addTo(map);

          marker.on('click', () => {
            skipMapPanRef.current = true;
            setActivePropertyId(property.id);

            setTimeout(() => {
              scrollToCard(property.id);
              setTimeout(() => { skipMapPanRef.current = false; }, 500);
            }, 50);
          });

          // if active, bring to top
          if (property.id === activePropertyId) marker.setZIndexOffset(1000);

          newMarkers.push(marker);
        } catch (error) {
          console.error('Error creating marker for property', property.id, error);
        }
      });

    setMarkers(newMarkers);

    // Fit bounds if no explicit searchLocation
    if (!searchLocation && filteredProperties.length > 0) {
      try {
        const coords = filteredProperties
          .filter(p => p.lat && p.lng)
          .map(p => [p.lat, p.lng]);

        if (coords.length > 0) {
          const bounds = L.latLngBounds(coords);
          map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 12
          });
        }
      } catch (error) {
        console.error('Error fitting bounds:', error);
        map.setView([17.3850, 78.4867], 11);
      }
    }
  }, [map, mapLoaded, filteredProperties]);

  useEffect(() => {
    if (!map || !markers.length) return;

    markers.forEach(m => {
      try {
        const pid = m.options.propertyId;
        const prop = filteredProperties.find(p => p.id === pid);
        if (prop) {
          m.setIcon(createMarkerIconForProperty(prop, pid === activePropertyId));
          m.setZIndexOffset(pid === activePropertyId ? 1000 : 0);
        }
      } catch (err) { console.error(err); }
    });

    // Only pan if we are not skipping due to marker click AND map is properly initialized
    if (!skipMapPanRef.current && map && map._container) {
      try {
        const activeProp = filteredProperties.find(p => p.id === activePropertyId);
        if (activeProp && activeProp.lat && activeProp.lng) {
          if (mapContainerRef.current && mapContainerRef.current.offsetParent !== null) {
            map.panTo([activeProp.lat, activeProp.lng], { animate: true, duration: 0.4 });
          }
        }
      } catch (error) {
        console.error('Error panning to property:', error);
      }
    }
  }, [activePropertyId, markers, map, filteredProperties]);

  // Utility: scroll to card (programmatic)
  const scrollToCard = (propertyId) => {
    try {
      const container = cardListRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-property-id="${propertyId}"]`);
      if (el) {
        isProgrammaticScrollRef.current = true;
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 450);
      }
    } catch (err) { console.error('scrollToCard error', err); }
  };

  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;

      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = setTimeout(() => {
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = requestAnimationFrame(() => {
          try {
            const rect = container.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const cards = Array.from(container.querySelectorAll('[data-property-id]'));
            if (!cards.length) return;

            let closest = null;
            let closestDistance = Infinity;
            cards.forEach(card => {
              const cRect = card.getBoundingClientRect();
              const cardCenter = cRect.left + cRect.width / 2;
              const distance = Math.abs(cardCenter - centerX);
              if (distance < closestDistance) {
                closestDistance = distance;
                closest = card;
              }
            });

            if (closest) {
              const id = closest.getAttribute('data-property-id');
              if (id && id !== activePropertyId && id !== lastActiveIdRef.current) {
                setActivePropertyId(id);
                lastActiveIdRef.current = id;
              }
            }
          } catch (err) {
            console.error('onScroll calc error', err);
          }
        });
      }, 150);
    };

    onScroll();

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      clearTimeout(scrollTimeoutRef.current);
      clearTimeout(scrollDebounceRef.current);
    };
  }, [filteredProperties]);

  useEffect(() => {
    const fetchSavedProperties = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/get-user-cart/${userId}/`);
        const savedItems = Array.isArray(response.data) ? response.data : [];
        setSavedProperties(savedItems.map(item => String(item.property_id)));
      } catch (error) {
        console.error('Error fetching saved properties:', error);
        setSavedProperties([]);
      }
    };
    if (userId) fetchSavedProperties();
  }, [userId]);

  // Fetch properties data
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const categoriesResponse = await axios.get(`${BASE_URL}/property-category/`);
        setCategories(categoriesResponse.data);

        const propertiesResponse = await axios.get(`${BASE_URL}/property/`);
        const filtered = propertiesResponse.data.filter(item =>
          item.status === true && item.type && item.type.toLowerCase().includes("rent")
        );

        const parsed = filtered.map(item => {
          const parseCoord = (coord) => {
            if (!coord) return null;
            const cleaned = coord.replace(/"/g, '').replace(':', '.');
            return parseFloat(cleaned);
          };

          const matchedCategory = categoriesResponse.data.find(
            cat => cat.category_id == item.category_id
          );

          const categoryName = matchedCategory ? matchedCategory.category : 'Property';

          const images = item.property_images?.length > 0
            ? item.property_images.map(img => `${BASE_URL}${img.image}`)
            : [buildingImage];

          return {
            id: String(item.property_id),
            title: categoryName,
            location: item.location || 'Not specified',
            price: `₹${item.price}`,
            date: item.created_at?.split('T')[0],
            facing: item.facing?.replace(/"/g, '') || 'N/A',
            area: item.site_area ? `${item.site_area} sq ft` : 'N/A',
            dimensions: item.roadwidth ? `${item.roadwidth} ft road` : 'N/A',
            listedBy: item.posted_by?.replace(/"/g, '') || 'Agent',
            lat: parseCoord(item.lat),
            lng: parseCoord(item.long),
            length: item.length,
            width: item.width,
            property_name: item.property_name,
            mobile_no: item.mobile_no,
            images: images,
            propertyData: item,
            owner: item.user_id,
            createdAt: item.created_at
          };
        });

        setProperties(parsed);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchProperties();
  }, []);

  // Filter properties based on selected type and price
  useEffect(() => {
    if (properties.length === 0) return;

    let filtered = (selectedType || priceFilter)
      ? properties.filter(p => {
        const typeMatch = !selectedType ||
          p.title.toLowerCase().includes(selectedType.toLowerCase());

        const priceMatch = !priceFilter ||
          (p.propertyData.price >= priceFilter.min &&
            p.propertyData.price <= priceFilter.max);

        return typeMatch && priceMatch;
      })
      : properties;

    const centerLat = searchLocation?.lat || 17.3875755;
    const centerLng = searchLocation?.lng || 78.3572111;
    filtered = sortPropertiesByDistance(filtered, centerLat, centerLng);

    setFilteredProperties(filtered);
  }, [properties, selectedType, priceFilter, searchLocation]);

  // API functions for cart operations
  const addToCart = async (propertyId) => {
    try {
      const response = await axios.post(`${BASE_URL}/user-cart/`, {
        user_id: userId,
        property_id: propertyId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (propertyId) => {
    try {
      const cartResponse = await axios.get(`${BASE_URL}/user-cart/?user_id=${userId}&property_id=${propertyId}`);
      if (cartResponse.data && cartResponse.data.length > 0) {
        await axios.delete(`${BASE_URL}/user-cart/${cartResponse.data[0].cart_id}/`);
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  const toggleSave = async (property) => {
    const propertyId = property.id;
    setSaving(prev => ({ ...prev, [propertyId]: true }));

    try {
      const isSaved = savedProperties.includes(propertyId);

      if (isSaved) {
        await removeFromCart(propertyId);
        setSavedProperties(prev => prev.filter(id => id !== propertyId));
      } else {
        await addToCart(propertyId);
        setSavedProperties(prev => [...prev, propertyId]);
      }
    } catch (error) {
      console.error('Error updating saved properties:', error);
    } finally {
      setSaving(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  const isSaved = (property) => savedProperties.includes(property.id);

  const toggleLike = (id) => {
    setLikedCards((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('selectedLocation');
    if (stored) setSearchLocation(JSON.parse(stored));
  }, []);

  const handleSelectedLocation = (locationData) => {
    setSearchLocation(locationData);
    
    // Immediately update the map if it exists
    if (map && locationData) {
      setTimeout(() => {
        try {
          map.panTo([locationData.lat, locationData.lng], { 
            animate: true, 
            duration: 1 
          });
          map.setZoom(13);
        } catch (error) {
          console.error('Error panning to searched location:', error);
        }
      }, 100);
    }
  };

  const handlePriceFilterChange = (range) => {
    setPriceFilter(range);
  };

  // FIXED: Clear search function
  const clearSearch = () => {
    localStorage.removeItem('savedSearchValue');
    localStorage.removeItem('selectedLocation');
    localStorage.removeItem('recentLocationSearches');
    setSearchLocation(null);
    setSelectedProperty(null);
    
    // Reset filters
    setPriceFilter(null);
    setSelectedType(null);
    
    // Remove search location marker
    if (window.searchLocationMarker && map) {
      map.removeLayer(window.searchLocationMarker);
      window.searchLocationMarker = null;
    }

    // Force map recreation
    setMapKey(prev => prev + 1);
    setMapLoaded(false);
  };

  return (
    <Box
      sx={{
        pb: 7,
        maxWidth: 480,
        mx: "auto",
        position: 'relative',
        backgroundColor: 'rgb(239, 231, 221)',
        minHeight: 600
      }}
    >

      {/* Search Bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'rgb(239, 231, 221)',
          px: 1,
          py: 1,
        }}
      >
        <CustomSearchBar
          onPlaceSelected={handleSelectedLocation}
          onClear={clearSearch}
          onPriceFilterChange={handlePriceFilterChange}
        />
      </Box>

      {/* Property Types */}
      <Box sx={{ px: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Rent Properties </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            pb: 1,
          }}
        >
          {rentalTypes.map((type, index) => (
            <Chip
              key={index}
              label={type}
              variant="filled"
              onClick={() => {
                setSelectedType(prev => (prev === type ? null : type));
              }}
              sx={{ 
                flexShrink: 0,
                bgcolor: selectedType === type ? '#000000' : 'transparent',
                color: selectedType === type ? '#ffffff' : '#000000',
                border: '1px solid black',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: selectedType === type ? '#000000' : 'rgba(0, 0, 0, 0.1)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* No properties message */}
      {(selectedType || priceFilter) && filteredProperties.length === 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          px: 2,
          textAlign: 'center'
        }}>
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '16px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Typography variant="h6">No properties found</Typography>
            
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedType && `Category: "${selectedType}"`}
              {selectedType && priceFilter && ' • '}
              {priceFilter && `Price: ${priceFilter.label}`}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Leaflet Map Container */}
      {(!((selectedType || priceFilter) && filteredProperties.length === 0)) && (   
        <Box sx={{ px: 2, pb: 10, position: 'relative' }}>
          {!isSubscribed && (
            <Box sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1000,
              backgroundColor: 'rgba(255,255,255,0.9)',
              padding: '8px 12px',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Typography variant="body2" sx={{ mr: 1 }}>Zoom in restricted for free users</Typography>
              <Button variant="contained" size="small" onClick={() => navigate('/user-subscription')}>
                Upgrade
              </Button>
            </Box>
          )}

          <div
            ref={mapContainerRef}
            key={`map-${mapKey}`}
            style={{
              width: '100vw',  
              height: 'calc(100vh - 220px)', 
              borderRadius: '6px',
              overflow: 'hidden',
              backgroundColor: '#f0f0f0',
              position: 'relative',
              marginLeft: '-16px', 
              marginRight: '-16px'
            }}
          >
            {!mapLoaded && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography>Loading map...</Typography>
              </Box>
            )}

            {/* Carousel - NOW USING visibleProperties INSTEAD OF filteredProperties */}
            {visibleProperties.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: -8,
                  zIndex: 1001,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  pointerEvents: 'auto',
                }}
              >
                {/* Left Arrow - NOW WITH INFINITE SCROLL */}
                <Button
                  onClick={handleLeftArrowClick}
                  sx={{
                    minWidth: 0,
                    width: 32,
                    height: 32,
                    p:1,
                    left:5,
                    borderRadius: '70%',
                    bgcolor: 'white',
                    boxShadow: 2,
                    mr: 1,
                    '&:hover': { bgcolor: 'grey.200' },
                    zIndex: 1002,
                  }}
                >
                  &lt;
                </Button>

                {/* Cards */}
                <Box
                  sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 2,
                    flexGrow: 1,
                    scrollSnapType: 'x mandatory',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                  ref={cardListRef}
                >
                  {visibleProperties.map((prop) => (
                    <Box
                      key={prop.id}
                      data-property-id={prop.id}
                      sx={{
                        flex: '0 0 280px',
                        scrollSnapAlign: 'center',
                        cursor: 'pointer',
                        height: '140px',
                      }}
                      onClick={() => {
                        setActivePropertyId(prop.id);
                        navigate(`/rent-description/${prop.id}`);
                      }}
                    >
                      <div style={{ padding: 6, height: '100%' }}>
                        <ReUsableCard
                          property={prop}
                          onCardClick={() => {
                            setActivePropertyId(prop.id);
                            navigate(`/rent-description/${prop.id}`);
                          }}
                          isSaved={isSaved}
                          toggleSave={toggleSave}
                          likedCards={likedCards}
                          toggleLike={toggleLike}
                          saving={saving[prop.id]}
                          isSubscribed={isSubscribed}
                          userType="Tenant"
                        />
                      </div>
                    </Box>
                  ))}
                </Box>

                {/* Right Arrow - NOW WITH INFINITE SCROLL */}
                <Button
                  onClick={handleRightArrowClick}
                  sx={{
                    minWidth: 0,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    boxShadow: 2,
                    p:1,
                    right:5,
                    ml: 1,
                    '&:hover': { bgcolor: 'grey.200' },
                    zIndex: 1002,
                  }}
                >
                  &gt;
                </Button>
              </Box>
            )}

            {/* Show message when no properties in current view */}
            {visibleProperties.length === 0 && filteredProperties.length > 0 && (
              <Box sx={{
                position: 'absolute',
                bottom: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1001,
                backgroundColor: 'rgba(255,255,255,0.9)',
                padding: '8px 16px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}>
                <Typography variant="body2">
                  No properties in current view. Zoom or pan to see properties.
                </Typography>
              </Box>
            )}
          </div>
        </Box>
      )}

      {/* Bottom Navigation */}
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <CustomBottomNav />
      </Paper>
    </Box>
  );
};

export default Rent_Property_Map;